import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import { firestore, collection, addDoc, doc, getDoc, updateDoc, Timestamp } from '../firebase-config';
import { Editor } from '@tinymce/tinymce-react';
import '../styles/BlogEditor.css';

const BlogEditor = () => {
  const { id } = useParams();
  const { loggedInUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const editorRef = useRef(null);
  
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!loggedInUser || loggedInUser.role !== 'Admin') {
      navigate('/');
    }
  }, [loggedInUser, navigate]);

  useEffect(() => {
    if (id) {
      const fetchPost = async () => {
        try {
          const postRef = doc(firestore, 'posts', id);
          const postSnap = await getDoc(postRef);
          
          if (postSnap.exists()) {
            const postData = postSnap.data();
            
            if (postData.authorId !== loggedInUser.uid) {
              setError('You can only edit your own blog posts.');
              return;
            }
            
            setTitle(postData.title);
            setContent(postData.content);
            setCategory(postData.category || '');
          } else {
            setError('Post not found.');
          }
        } catch (err) {
          console.error('Error fetching post:', err);
          setError('Failed to load post.');
        }
      };
      
      fetchPost();
    }
  }, [id, loggedInUser]);

  const handleSave = async () => {
    const editorContent = editorRef.current ? editorRef.current.getContent() : content;
    
    if (!title.trim() || !editorContent.trim() || !category.trim()) {
      alert('Please fill in all fields: title, content, and category.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const postData = {
        title,
        content: editorContent,
        category,
        authorId: loggedInUser.uid,
        authorName: loggedInUser.displayName || loggedInUser.email,
      };

      if (id) {
        const postRef = doc(firestore, 'posts', id);
        await updateDoc(postRef, {
          ...postData,
          updatedAt: Timestamp.now(),
        });
        alert('Blog post updated successfully!');
      } else {
        await addDoc(collection(firestore, 'posts'), {
          ...postData,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
        alert('Blog post created successfully!');
      }

      navigate('/admin-dashboard');
    } catch (err) {
      console.error('Error saving post:', err);
      setError('Failed to save post. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (error && id) {
    return (
      <div className="blog-editor">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/admin-dashboard')}>Back to Dashboard</button>
      </div>
    );
  }

  return (
    <div className="blog-editor">
      <h2>{id ? 'Edit Blog Post' : 'Create New Blog Post'}</h2>
      
      <div className="form-group">
        <label htmlFor="title">Title</label>
        <input
          id="title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter blog post title"
        />
      </div>

      <div className="form-group">
        <label htmlFor="category">Category</label>
        <input
          id="category"
          type="text"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          placeholder="e.g., Theology, Community, Announcements"
        />
      </div>

      <div className="form-group">
        <label>Content</label>
        <Editor
          apiKey="16nd2n35tc4ttll8gn95tptzucifo08um2cckdi6rbntk7ha"
          onInit={(evt, editor) => editorRef.current = editor}
          initialValue={content}
          init={{
            height: 500,
            menubar: false,
            plugins: [
              'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
              'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
              'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
            ],
            toolbar: 'undo redo | blocks | ' +
              'bold italic forecolor | alignleft aligncenter ' +
              'alignright alignjustify | bullist numlist outdent indent | ' +
              'removeformat | help',
            content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
          }}
        />
      </div>

      {error && <p className="error-message">{error}</p>}

      <div className="button-group">
        <button onClick={handleSave} disabled={loading}>
          {loading ? 'Saving...' : (id ? 'Update Post' : 'Create Post')}
        </button>
        <button onClick={() => navigate('/admin-dashboard')} className="cancel-btn">
          Cancel
        </button>
      </div>
    </div>
  );
};

export default BlogEditor;